import { API_BASE_URL } from '../../../config/api';

// Organizer form validation
export const validateOrganizerForm = (organizerData, setOrganizerErrors) => {
  console.log('📝 [FRONTEND] Validating organizer form with data:', {
    company: organizerData.company,
    vat_no: organizerData.vat_no,
    bank_name: organizerData.bank_name,
    bank_code: organizerData.bank_code,
    bank_account_name: organizerData.bank_account_name,
    bank_account_number: organizerData.bank_account_number ? '***' : ''
  });

  const errors = {};

  if (!organizerData.company.trim()) {
    errors.company = 'Company name is required';
  } else if (organizerData.company.trim().length < 3) {
    errors.company = 'Company name must be at least 3 characters';
  }

  // VAT is optional, but validate format if provided
  if (organizerData.vat_no.trim()) {
    if (!/^4\d{9}$/.test(organizerData.vat_no.replace(/\s/g, ''))) {
      errors.vat_no = 'VAT number must be 10 digits starting with 4';
    }
  }

  if (!organizerData.bank_name.trim()) {
    errors.bank_name = 'Bank name is required';
  }

  if (!organizerData.bank_code.trim()) {
    errors.bank_code = 'Bank code is required. Please re-select your bank.';
  }

  if (!organizerData.bank_account_name.trim()) {
    errors.bank_account_name = 'Bank account name is required';
  }

  if (!organizerData.bank_account_number.trim()) {
    errors.bank_account_number = 'Bank account number is required';
  } else {
    const accountNumber = organizerData.bank_account_number.replace(/\s/g, '');
    if (!/^\d+$/.test(accountNumber)) {
      errors.bank_account_number = 'Bank account number must contain only digits';
    } else if (accountNumber.length < 6) {
      errors.bank_account_number = 'Bank account number must be at least 6 digits';
    } else if (accountNumber.length > 20) {
      errors.bank_account_number = 'Bank account number must not exceed 20 digits';
    }
  }

  if (Object.keys(errors).length > 0) {
    console.log('❌ [FRONTEND] Form validation failed with errors:', errors);
  } else {
    console.log('✅ [FRONTEND] Form validation passed');
  }

  setOrganizerErrors(errors);
  return Object.keys(errors).length === 0;
};

// Handle organizer profile update
export const handleOrganizerUpdate = async (
  organizerData,
  setIsUpdatingOrganizer,
  setOrganizerUpdateMessage,
  setOrganizerErrors,
  setShowOrganizerForm,
  setShowOrganizerPopup,
  nav
) => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('❌ [FRONTEND] No auth token found in localStorage');
    return;
  }

  console.log('🚀 [FRONTEND] Starting organizer profile update...');
  console.log('📦 [FRONTEND] Payload being sent to backend:', {
    company: organizerData.company,
    vat_no: organizerData.vat_no,
    bank_name: organizerData.bank_name,
    bank_code: organizerData.bank_code,
    bank_account_name: organizerData.bank_account_name,
    bank_account_number: organizerData.bank_account_number ? '***' : ''
  });

  setIsUpdatingOrganizer(true);
  setOrganizerUpdateMessage('please wait while we prepare your account for organiser access');

  try {
    console.log('📡 [FRONTEND] Sending PUT request to /organiser/update-organizer-profile');
    const response = await fetch(`${API_BASE_URL}/organiser/update-organizer-profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        company: organizerData.company,
        vat_no: organizerData.vat_no,
        is_npo: organizerData.is_npo,
        bank_name: organizerData.bank_name,
        bank_code: organizerData.bank_code,
        bank_account_name: organizerData.bank_account_name,
        bank_account_number: organizerData.bank_account_number
      })
    });

    console.log('📡 [FRONTEND] Response status:', response.status, response.statusText);
    const result = await response.json();
    console.log('📡 [FRONTEND] Response body:', result);

    if (result.success) {
      console.log('✅ [FRONTEND] Organizer profile update succeeded');
      console.log('   paystackSubaccountCreated:', result.paystackSubaccountCreated);
      console.log('   paystackSubaccountCode:', result.paystackSubaccountCode);
      setOrganizerUpdateMessage('Account ready');
      setTimeout(() => {
        setIsUpdatingOrganizer(false);
        setOrganizerUpdateMessage('');
        setShowOrganizerForm(false);
        setShowOrganizerPopup(false);
        nav('/organiser-dash');
      }, 2000);
      return;
    }

    // Backend validation / business logic errors
    const fieldErrors = {};
    const errorMessage = result.error || result.details || 'Please check your information';

    if (response.status === 409 && result.error === 'Bank account already in use') {
      fieldErrors.bank_account_number = 'This bank account is already registered by another organizer. Please use a different account number.';
    } else if (response.status === 400 && result.error === 'Invalid bank account number') {
      fieldErrors.bank_account_number = result.details || 'Bank account number must contain only digits';
    } else if (response.status === 400 && result.error === 'Missing required fields') {
      fieldErrors._general = result.details || 'Please fill in all required fields';
    } else if (response.status === 400 && result.error === 'Paystack subaccount creation failed') {
      fieldErrors._general = result.details || 'Failed to create payment account. Please verify your bank details and try again.';
    } else if (response.status === 429) {
      fieldErrors._general = result.details || 'Too many attempts. Please wait a moment and try again.';
    }

    if (Object.keys(fieldErrors).length > 0) {
      // Map to inline form field errors and close loading popup
      setOrganizerErrors(fieldErrors);
      setIsUpdatingOrganizer(false);
      setOrganizerUpdateMessage('');
      console.error('❌ [FRONTEND] Organizer profile update failed — mapped to form fields:', fieldErrors);
    } else {
      // Unmapped error — keep popup open with Close button
      let displayMessage = errorMessage;
      if (result.paystackError) {
        displayMessage = result.paystackError;
      }
      console.error('❌ [FRONTEND] Organizer profile update failed:', {
        error: result.error,
        details: result.details,
        paystackError: result.paystackError,
        message: displayMessage
      });
      setOrganizerUpdateMessage(`Account error: ${displayMessage}`);
    }
  } catch (error) {
    console.error('❌ [FRONTEND] Network or fetch error during organizer update:', error);
    setOrganizerUpdateMessage('Account error, please check your connection and try again');
    // Keep popup open so user sees the error and can close it
  }
};
